import { HttpErrorResponse } from "@angular/common/http";
import { getStoredTranslations } from "@app/translation-cache";

export enum UploadErrorKind {
  OFFLINE = "offline",
  PDF_TOO_LARGE = "pdfTooLarge",
  INVALID_PDF = "invalidPdf",
  UNSUPPORTED_TYPE = "unsupportedType",
  CONVERSION_FAILED = "conversionFailed",
  INVALID_IMAGE = "invalidImage",
  UNKNOWN = "unknown",
}

export type UploadErrorContext = {
  kind: UploadErrorKind;
  fileName?: string;
  status?: number;
  rawMessage?: string;
};

export type UploadErrorModalData = {
  title: string;
  description: string;
  fileListTitle?: string;
  fileNames?: string[];
  reasonTitle: string;
  reason: string;
  solutionTitle: string;
  solutions: string[];
  closeLabel: string;
};

export type UploadErrorReasonParams = {
  fileName: string;
  maxSizeMb?: number;
};

export type UploadErrorMapperConfig = {
  localeId: string;
  maxSizeBytes?: number;
  unknownFileName: string;
  solutionPrefix: string;
  fallbackSolutions: string[];
  modalText: {
    title: string;
    descriptionSingle: (fileName: string) => string;
    descriptionMultiple: (count: number) => string;
    fileListTitle: string;
    reasonMultiple: string;
    reasonTitle: string;
    solutionTitle: string;
    closeLabel: string;
  };
  reasonByKind: Record<UploadErrorKind, (params: UploadErrorReasonParams) => string>;
};

export function createUploadError(
  kind: UploadErrorKind,
  fileName?: string,
  status?: number,
  rawMessage?: string,
): UploadErrorContext {
  return {
    kind,
    fileName,
    status,
    rawMessage,
  };
}

export function mapUploadError(error: unknown, fileName: string | undefined): UploadErrorContext {
  if (isUploadErrorContext(error)) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    const rawMessage = extractHttpErrorMessage(error);
    const status = error.status;

    if (status === 0 && !navigator.onLine) {
      return createUploadError(UploadErrorKind.OFFLINE, fileName, status, rawMessage);
    }

    if (status === 400) {
      return createUploadError(UploadErrorKind.INVALID_PDF, fileName, status, rawMessage);
    }

    if (status === 413) {
      return createUploadError(UploadErrorKind.PDF_TOO_LARGE, fileName, status, rawMessage);
    }

    if (status >= 500) {
      return createUploadError(UploadErrorKind.CONVERSION_FAILED, fileName, status, rawMessage);
    }

    return createUploadError(UploadErrorKind.UNKNOWN, fileName, status, rawMessage);
  }

  if (error instanceof Error) {
    if (error.message === "invalid-image" || error.message === "invalid-image-context") {
      return createUploadError(UploadErrorKind.INVALID_IMAGE, fileName);
    }
  }

  return createUploadError(UploadErrorKind.UNKNOWN, fileName);
}

export function buildUploadErrorModalData(
  context: UploadErrorContext,
  errorCount: number,
  config: UploadErrorMapperConfig,
  fileNames?: string[],
): UploadErrorModalData {
  const fileName = context.fileName ?? config.unknownFileName;
  const maxSizeMb = config.maxSizeBytes ? Math.round(config.maxSizeBytes / 1024 / 1024) : undefined;
  const resolver = config.reasonByKind[context.kind] ?? config.reasonByKind[UploadErrorKind.UNKNOWN];
  const isMultiple = errorCount > 1;
  const reason = isMultiple ? config.modalText.reasonMultiple : resolver({ fileName, maxSizeMb });
  const solutions = resolveSolutions(config);
  const uniqueFileNames = fileNames ? Array.from(new Set(fileNames)) : [];
  const hasFileList = isMultiple && uniqueFileNames.length > 0;

  return {
    title: config.modalText.title,
    description: isMultiple ? config.modalText.descriptionMultiple(errorCount) : config.modalText.descriptionSingle(fileName),
    fileListTitle: hasFileList ? config.modalText.fileListTitle : undefined,
    fileNames: hasFileList ? uniqueFileNames : undefined,
    reasonTitle: config.modalText.reasonTitle,
    reason,
    solutionTitle: config.modalText.solutionTitle,
    solutions,
    closeLabel: config.modalText.closeLabel,
  };
}

function resolveSolutions(config: UploadErrorMapperConfig) {
  const dynamicSolutions = getDynamicSolutions(config.solutionPrefix, config.localeId);
  return dynamicSolutions && dynamicSolutions.length > 0 ? dynamicSolutions : config.fallbackSolutions;
}

function getDynamicSolutions(prefix: string, localeId: string) {
  const translations = getTranslations(localeId);
  if (!translations) {
    return undefined;
  }
  const entries = Object.entries(translations).filter(([key, value]) => key.startsWith(prefix) && value);
  const sorted = entries.sort((a, b) => sortByNumericSuffix(a[0], prefix) - sortByNumericSuffix(b[0], prefix));
  return sorted.map(([, value]) => value);
}

function sortByNumericSuffix(key: string, prefix: string) {
  const suffix = key.slice(prefix.length);
  const num = Number.parseInt(suffix, 10);
  return Number.isFinite(num) ? num : Number.MAX_SAFE_INTEGER;
}

function getTranslations(localeId: string): Record<string, string> | undefined {
  return getStoredTranslations(localeId);
}

function extractHttpErrorMessage(error: HttpErrorResponse) {
  if (typeof error.error === "string") {
    return error.error;
  }
  if (typeof error.message === "string") {
    return error.message;
  }
  return "";
}

function isUploadErrorContext(error: unknown): error is UploadErrorContext {
  return typeof error === "object" && error !== null && "kind" in error;
}
